import strings from './DriveSpaceHelper.resx';
import { Locale } from '../locale/Locale';

/**
 * @public
 */
export interface IDisplayOptions {
    // Removes trailing zeroes after the decimal points
    trimDecimal?: boolean;

    // Returns empty string if the value is zero
    ignoreZero?: boolean;
}

const oneKiloByte = 1024;
const oneMegaByte = 1048576;
const oneGigaByte = 1073741824;
const oneTeraByte = 1099511627776;

// Regex to remove trailing zeroes (and optional '.').
const trimRegex = /\.?0+$/;

namespace DriveSpaceHelper {
    /**
     * Creates a display string for the given number in bytes.
     * This function produces a string value meant to mimic that displyed by file explorer.
     *
     * @public
     */
    export function getDisplayString(value: number, options: IDisplayOptions = {}): string {
        if (typeof value !== 'number' || isNaN(value) || (options.ignoreZero && value === 0) || value < 0) {
            return '';
        }

        let trimDecimal = options.trimDecimal || false;

        // 1 byte
        if (value === 1) {
            return strings.fileSizeBytesSingular;
        }

        // 0 bytes, 2 bytes - 1023 bytes
        if (value < oneKiloByte) {
            return _format(strings.fileSizeBytesPlural, value, trimDecimal);
        }

        // 1KB - 999 KB
        if (value < 1000 * oneKiloByte) {
            return _format(strings.fileSizeKB, value / oneKiloByte, trimDecimal);
        }

        // 0.97 MB - 999 MB
        if (value < 1000 * oneMegaByte) {
            return _format(strings.fileSizeMB, value / oneMegaByte, trimDecimal);
        }

        // 0.97 GB - 999 GB
        if (value < 1000 * oneGigaByte) {
            return _format(strings.fileSizeGB, value / oneGigaByte, trimDecimal);
        }

        // 0.97 TB ->
        return _format(strings.fileSizeTB, value / oneTeraByte, trimDecimal);
    }

    function _format(template: string, value: number, trimDecimal: boolean): string {
        return template.replace('{0}', _trimNumber(value, trimDecimal));
    }

    function _trimNumber(value: number, trimDecimal: boolean): string {
        // TODO: switch to toLocaleString for entire function when we update phantomJS
        // 12.00 -> 12
        if (value >= 100 || value === Math.floor(value)) {
            return value.toFixed(0);
        }

        // 45.678 -> 45.7
        // 4.5678 -> 4.57
        const str = value.toFixed(value >= 10 ? 1 : 2);
        // Since Number(num.toFixed) can trim decimals even when unwanted,
        // get the radix character and replace it manually
        return (trimDecimal ? str.replace(trimRegex, '') : str).replace('.', Locale.numberRadix);
    }
}

export default DriveSpaceHelper;
